import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FileText, Shield, AlertTriangle, DollarSign, Activity, Plus, Trash2, Clock, Database, CheckCircle, XCircle } from "lucide-react";
import LinkToCase from "@/components/shared/LinkToCase";

export default function InsuranceInvestigationPage() {
  const [tab, setTab] = useState("new");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [claimTypes, setClaimTypes] = useState({ policy_types: [], claim_types: [], fraud_indicators: [] });
  const [form, setForm] = useState({
    subject_name: "", subject_type: "individual",
    policy_number: "", policy_type: "auto", carrier_name: "", policy_limits: "", deductible: "", effective_date: "",
    claim_number: "", claim_type: "bodily_injury", date_of_loss: "", description_of_loss: "", claimed_amount: "",
    claimant_name: "", claimant_address: "", claimant_phone: "", claimant_email: "",
    prior_claims: [],
    include_fraud_analysis: true, include_subrogation: true, include_coverage_analysis: true, include_reserve_recommendation: true,
    adjuster_notes: "", jurisdiction: "",
  });

  useEffect(() => {
    axios.get(`${API}/insurance-investigation/claim-types`).then(r => setClaimTypes(r.data));
    axios.get(`${API}/insurance-investigation/reports`).then(r => setHistory(r.data.reports || []));
  }, []);

  const addPriorClaim = () => {
    setForm(f => ({...f, prior_claims: [...f.prior_claims, { date: "", claim_number: "", type: "", amount: 0, status: "settled", carrier: "", description: "" }]}));
  };

  const updatePriorClaim = (idx, field, val) => {
    setForm(f => {
      const claims = [...f.prior_claims];
      claims[idx] = {...claims[idx], [field]: val};
      return {...f, prior_claims: claims};
    });
  };

  const removePriorClaim = (idx) => {
    setForm(f => ({...f, prior_claims: f.prior_claims.filter((_, i) => i !== idx)}));
  };

  const generateReport = async () => {
    if (!form.subject_name || !form.description_of_loss) return;
    setLoading(true);
    try {
      const payload = {...form};
      if (payload.policy_limits) payload.policy_limits = parseFloat(payload.policy_limits);
      if (payload.deductible) payload.deductible = parseFloat(payload.deductible);
      if (payload.claimed_amount) payload.claimed_amount = parseFloat(payload.claimed_amount);
      payload.prior_claims = payload.prior_claims.map(c => ({...c, amount: parseFloat(c.amount) || 0}));
      const { data } = await axios.post(`${API}/insurance-investigation/generate`, payload);
      setReport(data);
      setTab("report");
      axios.get(`${API}/insurance-investigation/reports`).then(r => setHistory(r.data.reports || []));
    } catch (err) {
      alert(err.response?.data?.detail || "Report generation failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="insurance-investigation-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Insurance Investigation Report</h1>
        <p className="text-slate-500 mt-1">CLUE-style comprehensive loss underwriting and SIU analysis with live API verification</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="new" data-testid="tab-new-report">New Report</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report-view">Report</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-report-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Shield className="w-5 h-5 text-blue-600" />Subject & Policy Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Subject/Insured Name *</Label><Input data-testid="ins-subject-name" value={form.subject_name} onChange={e => setForm(f => ({...f, subject_name: e.target.value}))} placeholder="Full name" /></div>
                <div><Label>Policy Number</Label><Input data-testid="ins-policy-number" value={form.policy_number} onChange={e => setForm(f => ({...f, policy_number: e.target.value}))} placeholder="POL-XXXXXX" /></div>
                <div>
                  <Label>Policy Type</Label>
                  <Select value={form.policy_type} onValueChange={val => setForm(f => ({...f, policy_type: val}))}>
                    <SelectTrigger data-testid="policy-type-select" className="h-10">
                      <SelectValue placeholder="Select policy type" />
                    </SelectTrigger>
                    <SelectContent>
                      {claimTypes.policy_types.length > 0 ? (
                        claimTypes.policy_types.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)
                      ) : (
                        <>
                          <SelectItem value="auto">Auto/Vehicle</SelectItem>
                          <SelectItem value="homeowners">Homeowners/Renters</SelectItem>
                          <SelectItem value="commercial">Commercial Property</SelectItem>
                          <SelectItem value="workers_comp">Workers' Compensation</SelectItem>
                          <SelectItem value="general_liability">General Liability</SelectItem>
                          <SelectItem value="umbrella">Umbrella/Excess</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Insurance Carrier</Label><Input data-testid="ins-carrier" value={form.carrier_name} onChange={e => setForm(f => ({...f, carrier_name: e.target.value}))} placeholder="State Farm, GEICO..." /></div>
                <div><Label>Policy Limits ($)</Label><Input type="number" data-testid="ins-limits" value={form.policy_limits} onChange={e => setForm(f => ({...f, policy_limits: e.target.value}))} placeholder="100000" /></div>
                <div><Label>Deductible ($)</Label><Input type="number" data-testid="ins-deductible" value={form.deductible} onChange={e => setForm(f => ({...f, deductible: e.target.value}))} placeholder="500" /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-orange-500" />Current Claim Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Claim Type</Label>
                  <Select value={form.claim_type} onValueChange={val => setForm(f => ({...f, claim_type: val}))}>
                    <SelectTrigger data-testid="claim-type-select" className="h-10">
                      <SelectValue placeholder="Select claim type" />
                    </SelectTrigger>
                    <SelectContent>
                      {claimTypes.claim_types.length > 0 ? (
                        claimTypes.claim_types.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)
                      ) : (
                        <>
                          <SelectItem value="bodily_injury">Bodily Injury</SelectItem>
                          <SelectItem value="property_damage">Property Damage</SelectItem>
                          <SelectItem value="theft">Theft/Burglary</SelectItem>
                          <SelectItem value="fire">Fire/Smoke Damage</SelectItem>
                          <SelectItem value="liability">Liability/Negligence</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Date of Loss</Label><Input type="date" data-testid="ins-date-of-loss" value={form.date_of_loss} onChange={e => setForm(f => ({...f, date_of_loss: e.target.value}))} /></div>
                <div><Label>Claimed Amount ($)</Label><Input type="number" data-testid="ins-claimed-amount" value={form.claimed_amount} onChange={e => setForm(f => ({...f, claimed_amount: e.target.value}))} placeholder="75000" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Claimant Name</Label><Input data-testid="ins-claimant-name" value={form.claimant_name} onChange={e => setForm(f => ({...f, claimant_name: e.target.value}))} placeholder="If different from insured" /></div>
                <div><Label>Claimant Email</Label><Input data-testid="ins-claimant-email" value={form.claimant_email} onChange={e => setForm(f => ({...f, claimant_email: e.target.value}))} placeholder="claimant@example.com (verified via IPQS)" /></div>
                <div><Label>Claimant Phone</Label><Input data-testid="ins-claimant-phone" value={form.claimant_phone} onChange={e => setForm(f => ({...f, claimant_phone: e.target.value}))} placeholder="+1 555-0123 (verified via IPQS)" /></div>
                <div><Label>Claimant Address</Label><Input data-testid="ins-claimant-address" value={form.claimant_address} onChange={e => setForm(f => ({...f, claimant_address: e.target.value}))} placeholder="123 Main St (verified via Google)" /></div>
                <div><Label>Jurisdiction</Label><Input data-testid="ins-jurisdiction" value={form.jurisdiction} onChange={e => setForm(f => ({...f, jurisdiction: e.target.value}))} placeholder="State" /></div>
                <div><Label>Claim Number</Label><Input data-testid="ins-claim-number" value={form.claim_number} onChange={e => setForm(f => ({...f, claim_number: e.target.value}))} placeholder="CLM-XXXXXX" /></div>
              </div>
              <div>
                <Label>Description of Loss *</Label>
                <textarea className="w-full min-h-[100px] px-3 py-2 rounded-md border text-sm" data-testid="loss-description" value={form.description_of_loss} onChange={e => setForm(f => ({...f, description_of_loss: e.target.value}))} placeholder="Detailed description of the loss event, injuries, damages..." />
              </div>
              <div><Label>Adjuster Notes</Label><textarea className="w-full min-h-[60px] px-3 py-2 rounded-md border text-sm" data-testid="adjuster-notes" value={form.adjuster_notes} onChange={e => setForm(f => ({...f, adjuster_notes: e.target.value}))} placeholder="Any additional notes, red flags, or observations..." /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2"><Clock className="w-5 h-5 text-blue-600" />Prior Loss History</CardTitle>
                <Button variant="outline" size="sm" onClick={addPriorClaim} data-testid="add-prior-claim-btn"><Plus className="w-3 h-3 mr-1" />Add Claim</Button>
              </div>
            </CardHeader>
            <CardContent>
              {form.prior_claims.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No prior claims. Click "Add Claim" to add prior loss history.</p>
              ) : (
                <div className="space-y-3">
                  {form.prior_claims.map((claim, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg border flex flex-wrap gap-3 items-end">
                      <div><Label className="text-xs">Date</Label><Input className="h-8 w-32" type="date" value={claim.date} onChange={e => updatePriorClaim(idx, "date", e.target.value)} /></div>
                      <div><Label className="text-xs">Type</Label><Input className="h-8 w-32" value={claim.type} onChange={e => updatePriorClaim(idx, "type", e.target.value)} placeholder="BI, PD..." /></div>
                      <div><Label className="text-xs">Amount ($)</Label><Input className="h-8 w-24" type="number" value={claim.amount} onChange={e => updatePriorClaim(idx, "amount", e.target.value)} /></div>
                      <div><Label className="text-xs">Carrier</Label><Input className="h-8 w-28" value={claim.carrier} onChange={e => updatePriorClaim(idx, "carrier", e.target.value)} /></div>
                      <div>
                        <Label className="text-xs">Status</Label>
                        <Select value={claim.status} onValueChange={val => updatePriorClaim(idx, "status", val)}>
                          <SelectTrigger className="h-8 w-28 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="settled">Settled</SelectItem>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="denied">Denied</SelectItem>
                            <SelectItem value="litigated">Litigated</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1"><Label className="text-xs">Description</Label><Input className="h-8" value={claim.description} onChange={e => updatePriorClaim(idx, "description", e.target.value)} /></div>
                      <Button variant="ghost" size="sm" onClick={() => removePriorClaim(idx)} className="text-red-500 h-8"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg">Report Sections</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { key: "include_fraud_analysis", label: "Fraud Analysis" },
                  { key: "include_subrogation", label: "Subrogation" },
                  { key: "include_coverage_analysis", label: "Coverage Analysis" },
                  { key: "include_reserve_recommendation", label: "Reserve Recommendation" },
                ].map(s => (
                  <div key={s.key} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${form[s.key] ? "bg-blue-50 border-blue-300" : "border-slate-200"}`}>
                    <span className="text-sm font-medium">{s.label}</span>
                    <Switch
                      data-testid={`switch-${s.key}`}
                      checked={form[s.key]}
                      onCheckedChange={checked => setForm(f => ({...f, [s.key]: checked}))}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600">
            <div className="flex items-center gap-2 font-medium text-slate-800 mb-1"><Database className="w-4 h-4 text-blue-600" />Live API Verification</div>
            <p>Claimant email, phone, and address will be verified through IPQS, Hunter.io, and Google before report generation. Provide claimant contact details for real-time fraud analysis.</p>
          </div>

          <Button data-testid="generate-report-btn" onClick={generateReport} disabled={loading || !form.subject_name || !form.description_of_loss} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-lg">
            {loading ? <><Activity className="w-5 h-5 animate-spin mr-2" />Generating CLUE Report (verifying data)...</> : <><FileText className="w-5 h-5 mr-2" />Generate Insurance Investigation Report</>}
          </Button>
        </TabsContent>

        <TabsContent value="report" className="mt-4">
          {report ? (
            <div className="space-y-4">
              <Card className="border-l-4 border-l-blue-600">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h2 className="text-xl font-bold">{report.subject?.name}</h2>
                      <p className="text-slate-500 text-sm">
                        {report.policy?.carrier || "Unknown Carrier"} | {report.policy?.type} | Claim: {report.claim?.type}
                        {report.claim?.claimed_amount && ` | $${report.claim.claimed_amount.toLocaleString()}`}
                      </p>
                    </div>
                    <span className="text-xs text-slate-400">{new Date(report.created_at).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Real API Verification Results */}
              {report.data_sources_used?.length > 0 && (
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader><CardTitle className="text-lg text-blue-800 flex items-center gap-2"><Database className="w-5 h-5" />Live API Verification ({report.total_apis_queried} sources)</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {report.data_sources_used.map((s, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{s}</span>
                      ))}
                    </div>

                    {report.verification_results?.email_fraud && (
                      <div className="p-3 bg-white rounded-lg border text-sm">
                        <h4 className="font-semibold text-slate-800 mb-1">Email Fraud Check (IPQS)</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div><span className="text-slate-500">Fraud Score:</span> <span className={`font-bold ${report.verification_results.email_fraud.fraud_score >= 75 ? "text-red-600" : report.verification_results.email_fraud.fraud_score >= 50 ? "text-orange-500" : "text-green-600"}`}>{report.verification_results.email_fraud.fraud_score}/100</span></div>
                          <div><span className="text-slate-500">Disposable:</span> <span className={report.verification_results.email_fraud.disposable ? "text-red-600 font-bold" : "text-green-600"}>{String(report.verification_results.email_fraud.disposable)}</span></div>
                          <div><span className="text-slate-500">Leaked:</span> <span className={report.verification_results.email_fraud.leaked ? "text-red-600 font-bold" : "text-green-600"}>{String(report.verification_results.email_fraud.leaked)}</span></div>
                          <div><span className="text-slate-500">Deliverability:</span> {report.verification_results.email_fraud.deliverability}</div>
                        </div>
                      </div>
                    )}

                    {report.verification_results?.email_verification && (
                      <div className="p-3 bg-white rounded-lg border text-sm">
                        <h4 className="font-semibold text-slate-800 mb-1">Email Verification (Hunter.io)</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div><span className="text-slate-500">Status:</span> <span className={`font-bold ${report.verification_results.email_verification.status === "valid" ? "text-green-600" : "text-red-600"}`}>{report.verification_results.email_verification.status}</span></div>
                          <div><span className="text-slate-500">Score:</span> {report.verification_results.email_verification.score}%</div>
                          <div><span className="text-slate-500">Webmail:</span> {String(report.verification_results.email_verification.webmail)}</div>
                          <div><span className="text-slate-500">MX Records:</span> {String(report.verification_results.email_verification.mx_records)}</div>
                        </div>
                      </div>
                    )}

                    {report.verification_results?.phone_fraud && (
                      <div className="p-3 bg-white rounded-lg border text-sm">
                        <h4 className="font-semibold text-slate-800 mb-1">Phone Analysis (IPQS)</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div><span className="text-slate-500">Fraud Score:</span> <span className={`font-bold ${report.verification_results.phone_fraud.fraud_score >= 75 ? "text-red-600" : "text-green-600"}`}>{report.verification_results.phone_fraud.fraud_score}/100</span></div>
                          <div><span className="text-slate-500">Valid:</span> {String(report.verification_results.phone_fraud.valid)}</div>
                          <div><span className="text-slate-500">Carrier:</span> {report.verification_results.phone_fraud.carrier}</div>
                          <div><span className="text-slate-500">Prepaid:</span> <span className={report.verification_results.phone_fraud.prepaid ? "text-red-600 font-bold" : "text-green-600"}>{String(report.verification_results.phone_fraud.prepaid)}</span></div>
                        </div>
                      </div>
                    )}

                    {report.verification_results?.address_verification && (
                      <div className="p-3 bg-white rounded-lg border text-sm">
                        <h4 className="font-semibold text-slate-800 mb-1">Address Verification (Google)</h4>
                        <div className="text-xs">
                          <span className="text-slate-500">Verified:</span>{" "}
                          <span className={`font-bold ${report.verification_results.address_verification.verified ? "text-green-600" : "text-red-600"}`}>
                            {String(report.verification_results.address_verification.verified)}
                          </span>
                          {report.verification_results.address_verification.formatted_address && (
                            <span className="ml-3 text-slate-600">({report.verification_results.address_verification.formatted_address})</span>
                          )}
                        </div>
                      </div>
                    )}

                    {report.api_fraud_indicators?.length > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />API-Detected Fraud Indicators</h4>
                        <ul className="space-y-1">
                          {report.api_fraud_indicators.map((f, i) => (
                            <li key={i} className="text-red-700 text-xs flex items-start gap-2"><XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />{f}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardContent className="pt-6">
                  <div className="prose prose-sm max-w-none">
                    <div className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="report-content">{report.report_content}</div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => window.print()} data-testid="print-report-btn">Print Report</Button>
                <Button variant="outline" onClick={() => { setTab("new"); setReport(null); }} data-testid="new-report-btn">New Report</Button>
              </div>

              <LinkToCase
                itemId={report.id}
                itemType="insurance_report"
                title={`Insurance Report: ${report.subject?.name}`}
                summary={`${report.policy?.carrier || 'Unknown'} | ${report.claim?.type} | $${(report.claim?.claimed_amount || 0).toLocaleString()} | ${report.total_apis_queried || 0} APIs verified`}
                metadata={{ claim_type: report.claim?.type, data_sources: report.data_sources_used }}
              />
            </div>
          ) : (
            <Card><CardContent className="py-12 text-center text-slate-500">No report generated yet. Fill in the claim details and generate a report.</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-500">No reports generated yet.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {history.map(r => (
                <Card key={r.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setReport(r); setTab("report"); }}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div>
                      <span className="font-semibold">{r.subject?.name}</span>
                      <span className="text-slate-500 text-sm ml-2">{r.policy?.carrier} — {r.claim?.type}</span>
                      {r.claim?.claimed_amount && <span className="text-sm ml-2 font-medium text-amber-600">${r.claim.claimed_amount.toLocaleString()}</span>}
                      {r.total_apis_queried > 0 && <span className="text-xs ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{r.total_apis_queried} APIs</span>}
                    </div>
                    <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
